<template>
  <dropdown
    ref="dropdownRef"
    :force-visibility="showDropdown"
    title="Statuses"
    @open="handleDropdownOpen()"
  >
    <base-ifp-filter
      label="Status"
      clearable
      @clear-clicked="handleClearClick()"
    >
      <template #value-display>
        <template v-if="hasOneSelectedStatusName">
          <status-chicklet :status="selectedStatuses[0]" />
        </template>
        <template v-else>
          {{ selectedStatusesLabel }}
        </template>
      </template>
    </base-ifp-filter>
    <template #content>
      <div>
        <sleek-filter-input
          v-if="showFilterInput"
          ref="sleekFilterInputRef"
          v-model="filterQuery"
          placeholder="Search by name…"
          show-small-text
        />
        <ul>
          <li v-for="status in filteredDisplayableActiveStatusOptions" :key="status.id" @click="toggleSelectedStatus(status)">
            <span :class="[{'selected' : isActiveSelected || isSelected(status.id) }, {'muted' : isActiveSelected}]">
              <status-chicklet :status="status" />
            </span>
          </li>
          <li v-for="status in filteredDisplayableInactiveStatusOptions" :key="status.id" @click="toggleSelectedStatus(status)">
            <span :class="[{ 'selected' : isInactiveSelected || isSelected(status.id) }, {'muted' : isInactiveSelected}]">
              <status-chicklet :status="status" />
            </span>
          </li>
        </ul>
        <hr >
        <ul>
          <li @click="toggleStatusType('active')">
            <span :class="{'selected': isActiveSelected}"><i class="ddi ddi-active" /><span>Active statuses</span></span>
          </li>
          <li @click="toggleStatusType('inactive')">  
            <span :class="{'selected': isInactiveSelected}"><i class="ddi ddi-inactive" /><span>Inactive statuses</span></span>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { clone } from 'lodash-es'

/* Mixins */
import SearchableDropdownMixin from '@/mixins/searchable-dropdown.mixin'

/* Components */

/* Helpers */

/* Data types */
import { IStatusForSelection } from '@/api/types/response.types'
import { StatusTypeValues } from '@/types'

/* API */

export default defineComponent({
  name: 'IfpFilterStatuses',

  extends: SearchableDropdownMixin,

  props: {
    statusType: {
      type: String,
      default: '',
    },
    activeStatusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },
    inactiveStatusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },
  },

  emits: ['status-type-updated'],

  data() {
    return {
      showDropdown: false,

      /* SearchableDropdownMixin configuration */
      allowMultipleSelections: true,
    }
  },

  computed: {
    totalOptionsCount(): number {
      return this.displayableStatusOptions.length
    },
    filteredDisplayableActiveStatusOptions(): IStatusForSelection[] {
      return this.displayableActiveStatusOptions.filter(status => {
        return status.name
          .toLowerCase()
          .includes(this.filterQuery.toLowerCase())
      })
    },
    filteredDisplayableInactiveStatusOptions(): IStatusForSelection[] {
      return this.displayableInactiveStatusOptions.filter(status => {
        return status.name
          .toLowerCase()
          .includes(this.filterQuery.toLowerCase())
      })
    },
    // If showing multiple workflows, status names can overlap. Let's extract
    // distinct names (using the first instance if multiple).
    displayableActiveStatusOptions(): IStatusForSelection[] {
      return this.activeStatusOptions.filter(
        (s, i, arr) =>
          arr.findIndex(t => t.name.trim() === s.name.trim()) === i,
      )
    },
    displayableInactiveStatusOptions(): IStatusForSelection[] {
      return this.inactiveStatusOptions
        .filter(status => {
          return status.name
            .toLowerCase()
            .includes(this.filterQuery.toLowerCase())
        })
        .filter(
          (s, i, arr) =>
            arr.findIndex(t => t.name.trim() === s.name.trim()) === i,
        )
    },
    displayableStatusOptions(): IStatusForSelection[] {
      return this.displayableActiveStatusOptions.concat(
        this.displayableInactiveStatusOptions,
      )
    },
    isActiveSelected(): boolean {
      return this.statusType === StatusTypeValues.ACTIVE
    },
    isInactiveSelected(): boolean {
      return this.statusType === StatusTypeValues.INACTIVE
    },
    allStatusOptions(): IStatusForSelection[] {
      return this.activeStatusOptions.concat(this.inactiveStatusOptions)
    },
    selectedStatuses(): IStatusForSelection[] {
      return this.allStatusOptions.filter(s => this.selectedIds.includes(s.id))
    },
    selectedStatusNames(): string[] {
      const statusNames = this.selectedStatuses.map(s => s.name)
      return Array.from(new Set(statusNames))
    },
    hasSelectedStatuses(): boolean {
      if (this.selectedIds.length === 0) {
        return false
      }
      return this.allStatusOptions.some(s => this.selectedIds.includes(s.id))
    },
    hasOneSelectedStatusName(): boolean {
      return this.selectedStatusNames.length === 1
    },
    selectedStatusesLabel(): string {
      if (!this.hasSelectedStatuses) {
        switch (this.statusType) {
          case StatusTypeValues.ACTIVE:
            return 'Active'
          case StatusTypeValues.INACTIVE:
            return 'Inactive'
          default:
            return ''
        }
      }
      if (this.selectedStatusNames.length > 1) {
        return `${this.selectedStatusNames.length} selected`
      }
      return this.selectedStatuses[0].name
    },
  },

  created() {
    // At start, rebuild the selected IDs in case new statuses of
    // similar names have been added (or some have been removed -- though you can't remove published statuses currently).
    // Gotcha: Custom filters (e.g. from a previous saved session) would then not match
    // since the itemTraitSet would change so user would have to rename this filter upon creation.
    // Note: If a status name is changed, it will reflect regardless, since we're referencing the ids
    let cleansedSelectedIDs = [] as number[]
    this.internalSelectedIDs.forEach(id => {
      const curStatus = this.getStatus(id)
      if (curStatus === null) {
        return
      }
      cleansedSelectedIDs = cleansedSelectedIDs.concat(
        this.statusIDsMatchingStatusNameAndType(curStatus),
      )
    })
    this._updateSelectedIDs(
      cleansedSelectedIDs.filter(
        (s, i, arr) => arr.findIndex(t => t === s) === i,
      ),
    )
  },

  methods: {
    closeDropdown() {
      this.showDropdown = true
      this.$nextTick(() => {
        this.showDropdown = false
      })
    },
    getStatus(id: number): IStatusForSelection {
      if (this.isActiveStatus(id)) {
        return this.activeStatusOptions.find(s => s.id === id)
      } else if (this.isInactiveStatus(id)) {
        return this.inactiveStatusOptions.find(s => s.id === id)
      }
      return null
    },
    isActiveStatus(id: number): boolean {
      return this.activeStatusOptions.some(s => s.id === id)
    },
    isInactiveStatus(id: number): boolean {
      return this.inactiveStatusOptions.some(s => s.id === id)
    },
    statusIDsMatchingStatusNameAndType(
      selectedStatus: IStatusForSelection,
    ): number[] {
      if (this.isActiveStatus(selectedStatus.id)) {
        return this.activeStatusOptions
          .filter(s => s.name.trim() === selectedStatus.name.trim())
          .map(s => s.id)
      }
      if (this.isInactiveStatus(selectedStatus.id)) {
        return this.inactiveStatusOptions
          .filter(s => s.name.trim() === selectedStatus.name.trim())
          .map(s => s.id)
      }
    },
    toggleSelectedStatus(status: IStatusForSelection) {
      const identicallyNamedStatusIDs = this.statusIDsMatchingStatusNameAndType(
        status,
      )
      let newSelectedStatusIDs = clone(this.internalSelectedIDs)

      if (this.isSelected(status.id)) {
        newSelectedStatusIDs = newSelectedStatusIDs.filter(
          s => !identicallyNamedStatusIDs.includes(s),
        )
      } else {
        newSelectedStatusIDs.push(...identicallyNamedStatusIDs)
      }

      const distinctStatusIDs = Array.from(new Set(newSelectedStatusIDs))

      if (this.isActiveSelected || this.isInactiveSelected) {
        // If going from "Active" or "Inactive" to a singleselected status,
        // update both the 'statusType' and the 'selectedIDs'
        // immediately to prevent a double call with the normal debounce
        this.clearStatusType()
        this._updateSelectedIDs(distinctStatusIDs, true)
      } else {
        this._updateSelectedIDs(distinctStatusIDs)
      }
    },
    toggleStatusType(type: string) {
      this.clearSelectedIDs()
      if (this.statusType === type) {
        this.clearStatusType()
      } else {
        this.updateStatusType(type)
      }
    },
    updateStatusType(val: string) {
      this.$emit('status-type-updated', val)
    },
    clearStatusType() {
      this.updateStatusType('')
    },
    handleClearClick() {
      this.clearSelectedIDs()
      this.clearStatusType()
    },
  },
})
</script>
